/**
 * Per-entry provider configuration.
 *
 * Each static HTML page mounts <App> with a `provider` prop. The provider
 * controls two UX additions on top of the generic flow:
 *
 *   1. A pre-credentials intro banner naming the third-party service.
 *   2. A post-discovery (and post-pick for multi-unit accounts) consent modal
 *      that the user must explicitly Accept before /oauth/login is called.
 *
 * Today only Google requires consent; the generic 'shelly' provider skips
 * both. To add another provider:
 *
 *   1. Append a row to PROVIDERS below.
 *   2. Create src/main_<provider>.ts mirroring src/main_google.ts, passing
 *      `provider: '<provider>'` as a prop.
 *   3. Create oauth_login_<provider>.html as a copy of oauth_login.html with
 *      its <script> pointing at the new entry.
 *   4. Add the new HTML to rollupOptions.input in vite.config.ts.
 */

export type Provider = 'shelly' | 'google';

export interface ProviderConfig {
	id: Provider;
	displayName: string;
	requiresConsent: boolean;
	/**
	 * Provider's public privacy policy URL. When set, rendered as a link
	 * on the consent screen so the user can review what the third party
	 * does with the data they're about to grant access to. Recommended
	 * by Google's account-linking design guidelines.
	 */
	privacyPolicyUrl?: string;
}

export const PROVIDERS: Record<Provider, ProviderConfig> = {
	shelly: { id: 'shelly', displayName: 'Shelly', requiresConsent: false },
	google: {
		id: 'google',
		displayName: 'Google',
		requiresConsent: true,
		privacyPolicyUrl: 'https://policies.google.com/privacy',
	},
};

/**
 * Shelly account-management URL where users can review and unlink
 * third-party integrations. Surfaced on the consent screen to satisfy
 * Google's "ability to unlink" design recommendation.
 */
export const SHELLY_MANAGE_INTEGRATIONS_URL = 'https://control.shelly.cloud/#/integrations';

export function getProviderConfig(provider: Provider): ProviderConfig {
	return PROVIDERS[provider];
}
